import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Button, Icon, Select, SelectOption, Spinner } from "@metsooutotec/modes-react-components"
import { SVGFromJson } from "./utils/svg-to-json"
import { parseSync } from "svgson"
import styles from "./provision-events-svg-viewer.module.css"
import { SelectValue } from "components/common/select-component-helper"
import { enrichProvisionSvg } from "./utils/svg-enricher"
import { EquipmentSelectedCbType, EquipmentType, SelectedElement } from "../provisions-utils/provision-types"
import { Asset } from "features/organizations/assets/models/asset-models"
import { getAssetSectionSVG } from "features/organizations/asset-sections/services/asset-section-services"
import { CssSVGClasses } from "features/organizations/asset-sections/pages/editor/editor-utils/editor-types"
import { GetLinerId, StripLastSegment } from "features/organizations/asset-sections/pages/editor/editor-utils/id-string-utils"

type ProvisionEventsEventViewSVGLoadedProps = {
    asset: Asset
    assetSectionId: string
    activeEquipmentType?: EquipmentType
    requireEquipmentOptionToBeSelected?: boolean
    allowAllEquipmentSelection?: boolean
    selectedElements?: SelectedElement[]
    equipmentOptions?: SelectOption[]
    onEquipmentSelected?: EquipmentSelectedCbType
}

type SvgElementSelectionInfo = {
    x: number
    y: number
    elementType: EquipmentType
    linerIds: string[]
    sensorId?: string
    isSelectAll: boolean
    materialId?: string
}

export const ProvisionEventsSVGViewer = ({
    onEquipmentSelected,
    asset,
    requireEquipmentOptionToBeSelected,
    allowAllEquipmentSelection,
    assetSectionId,
    selectedElements,
    equipmentOptions,
    activeEquipmentType,
}: ProvisionEventsEventViewSVGLoadedProps) => {
    const [zoomLevel, setZoomLevel] = useState(1)
    const [tooltipText, setTooltipText] = useState<string>("")
    const [materialSelectorInfo, setMaterialSelectorInfo] = useState<SvgElementSelectionInfo>()

    const svgContainerRef = useRef<HTMLDivElement>(null)

    const [cleanSvg, setCleanSvg] = useState<string | null>(null)
    const [isLoadingAssetImage, setLoadingAssetImage] = useState<boolean>(false)

    useEffect(() => {
        const fetchImage = async () => {
            setLoadingAssetImage(true)
            const imageUrl = await getAssetSectionSVG(assetSectionId)

            const response = await fetch(imageUrl)
            const content = await response.text()

            setCleanSvg(content)
            setLoadingAssetImage(false)
        }
        fetchImage()
    }, [assetSectionId])

    const { assetSectionImage } = useMemo(() => {
        if (!cleanSvg) {
            return { assetSectionImage: "" }
        }

        const assetSection = asset.assetSections.find(s => s.id === assetSectionId) ?? asset.assetSections[0]
        return enrichProvisionSvg(cleanSvg, selectedElements ?? [], assetSection.sensorNotificationArea)
    }, [cleanSvg, selectedElements])

    const handleZoomIn = useCallback(() => setZoomLevel(zoomLevel + 0.1), [zoomLevel])

    const handleZoomOut = useCallback(() => {
        if (zoomLevel > 0.2) {
            setZoomLevel(zoomLevel - 0.1)
        }
    }, [zoomLevel])

    const handleZoomReset = useCallback(() => {
        setZoomLevel(1)
    }, [])

    const onMouseOver = useCallback((e: React.MouseEvent<HTMLElement, MouseEvent>) => {
        const path = e.target as SVGElement
        const pathId = path.id ?? ""

        setTooltipText("")
        if (path instanceof SVGPathElement) {
            if (path.classList.contains(CssSVGClasses.WsLiner)) {
                const linerName = GetLinerId(pathId)
                setTooltipText(`Liner Id: ${linerName as string}`)
            } else if (path.classList.contains(CssSVGClasses.WsHole)) {
                const holeName = StripLastSegment(pathId)
                setTooltipText(`Hole Id: ${holeName as string}`)
            }
        }
    }, [])

    const handleElementClick = useCallback(
        (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
            if (!onEquipmentSelected) {
                return
            }
            if (!activeEquipmentType) {
                throw new Error("activeEquipmentType is not defined")
            }

            const { x, y } = resolveMaterialSelectorCoordinates(e)
            const { clientX, clientY } = e

            const isTriggeredBySensorElement = (e.target as SVGElement).classList.contains(CssSVGClasses.WsHole)
            if (activeEquipmentType === "Sensor" && !isTriggeredBySensorElement) {
                // when configuring sensors, the primary element must be a sensor
                // When configuring liners, we allow the primary element to be a sensor, the liner is resolved "underneath" the sensor
                return
            }

            const elementsAtPoint = document.elementsFromPoint(clientX, clientY)
            const linerLocationElement = elementsAtPoint.find(e => e.classList.contains(CssSVGClasses.WsLiner)) as SVGElement
            const sensorLocationElement = elementsAtPoint.find(e => e.classList.contains(CssSVGClasses.WsHole)) as SVGElement

            if (!linerLocationElement) {
                return
            }

            const selectedElement = selectedElements?.find(e =>
                activeEquipmentType === "Liner"
                    ? e.linerSvgReference === linerLocationElement.id
                    : e.sensorSvgReference === sensorLocationElement?.id,
            )

            if (requireEquipmentOptionToBeSelected) {
                setMaterialSelectorInfo({
                    x,
                    y,
                    elementType: activeEquipmentType,
                    linerIds: [linerLocationElement.id],
                    sensorId: activeEquipmentType === "Sensor" ? sensorLocationElement?.id : undefined,
                    materialId: selectedElement?.equipmentOptionId,
                    isSelectAll: false,
                })
            } else {
                onEquipmentSelected(
                    activeEquipmentType,
                    [linerLocationElement.id],
                    activeEquipmentType === "Sensor" ? sensorLocationElement?.id : undefined,
                    false,
                    selectedElement?.equipmentOptionId,
                )
            }
        },
        [onEquipmentSelected, activeEquipmentType],
    )

    const handleContextMenuClick = useCallback(
        (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
            e.preventDefault()

            if (!allowAllEquipmentSelection) {
                return
            }

            const { x, y } = resolveMaterialSelectorCoordinates(e)

            if (!activeEquipmentType) {
                throw new Error("activeEquipmentType is not defined")
            }

            const elementClass = activeEquipmentType === "Liner" ? CssSVGClasses.WsLiner : CssSVGClasses.WsHole

            const liners = svgContainerRef.current?.getElementsByClassName(elementClass)
            const elementIds = [...(liners ?? [])].map(e => e.id)

            setMaterialSelectorInfo({ x, y, elementType: activeEquipmentType, linerIds: elementIds, isSelectAll: true })
        },
        [activeEquipmentType, allowAllEquipmentSelection],
    )

    const resolveMaterialSelectorCoordinates = ({ clientX, clientY }: { clientX: number; clientY: number }) => {
        // Asset navigation sidebar component forces x and y to be in relation to its pusher container
        // hence we need the container in order to adjust the popup position accordingly
        const transformBox = svgContainerRef.current?.closest(".pusher")?.getBoundingClientRect()

        const x = clientX - (transformBox?.x ?? 0)
        const y = clientY - (transformBox?.y ?? 0)

        return { x, y }
    }

    const handleLinerSubmit = useCallback<EquipmentSelectedCbType>(
        (
            equipmentType: EquipmentType,
            linerReferenceIdList: string[],
            sensorReferenceId: string | undefined,
            isSelectAll: boolean,
            materialId?: string,
        ) => {
            if (onEquipmentSelected) {
                onEquipmentSelected(equipmentType, linerReferenceIdList, sensorReferenceId, isSelectAll, materialId)
                setMaterialSelectorInfo(undefined)
            }
        },
        [onEquipmentSelected],
    )

    const handleCancelLinerSelection = useCallback(() => {
        setMaterialSelectorInfo(undefined)
    }, [materialSelectorInfo])

    const renderWrappedLoader = (message: string) => {
        return (
            <div className={styles.loaderSpinnerContainer}>
                <Spinner text={message} />
            </div>
        )
    }

    if (isLoadingAssetImage) {
        return renderWrappedLoader("Loading svg...")
    }

    if (!assetSectionImage) {
        return renderWrappedLoader("No image found.")
    }

    return (
        <>
            <div className={styles.viewerContainer} ref={svgContainerRef}>
                <div className={styles.svgContainer} onContextMenu={handleContextMenuClick}>
                    <div
                        onClick={handleElementClick}
                        onMouseOver={onMouseOver}
                        style={{
                            transform: `scale(${zoomLevel})`,
                            transformOrigin: "top left",
                        }}
                    >
                        {assetSectionImage && <SVGFromJson json={parseSync(assetSectionImage)} />}
                    </div>
                </div>

                <div className={styles.tooltipText}>{tooltipText}</div>
                <div className={styles.footer}>
                    <Icon className='icon-zoom-out-24 zoom-button' onClick={handleZoomOut} />
                    <Icon className='icon-refresh-24 zoom-button' onClick={handleZoomReset} />
                    <Icon className='icon-zoom-in-24 zoom-button' onClick={handleZoomIn} />
                </div>
                {materialSelectorInfo && (
                    <LinerMaterialSelector
                        elementInfo={materialSelectorInfo}
                        options={equipmentOptions ?? []}
                        onSubmitSelection={handleLinerSubmit}
                        onCancelLinerSelection={handleCancelLinerSelection}
                    />
                )}
            </div>
        </>
    )
}

const LinerMaterialSelector = ({
    elementInfo,
    options,
    onSubmitSelection,
    onCancelLinerSelection,
}: {
    elementInfo: SvgElementSelectionInfo
    options: SelectOption[]
    onSubmitSelection: EquipmentSelectedCbType
    onCancelLinerSelection: () => void
}) => {
    const [position, setPosition] = useState({ left: "0px", top: "0px" })

    const modalRef = useRef<HTMLDivElement | undefined>()

    const onRefChange = useCallback((node: HTMLDivElement) => {
        if (!node) {
            document.removeEventListener("mousedown", outsideClickListener)
            return
        }

        document.addEventListener("mousedown", outsideClickListener)

        const modalBox = node.getBoundingClientRect()
        const offsetY = modalBox?.height ?? 0
        const offsetX = (modalBox?.width ?? 0) / 2

        setPosition({
            top: `${elementInfo.y - offsetY}px`,
            left: `${elementInfo.x - offsetX}px`,
        })
        modalRef.current = node
    }, [])

    const outsideClickListener = useCallback(
        (e: MouseEvent) => {
            if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
                onCancelLinerSelection()
            }
        },
        [modalRef],
    )

    const [selectedOption, setSelectedOption] = useState<string>(elementInfo.materialId ?? "")

    const optionChangeHandler = useCallback(
        (value: SelectValue) => {
            setSelectedOption(value as string)
        },
        [selectedOption],
    )

    const submitSelection = useCallback(
        () => onSubmitSelection(elementInfo.elementType, elementInfo.linerIds, elementInfo.sensorId, false, selectedOption),
        [selectedOption, onSubmitSelection],
    )

    const resolveLabel = useCallback(
        (option: SvgElementSelectionInfo) => {
            if (option.elementType === "Liner") {
                return elementInfo.isSelectAll ? "Select material for all liners" : `Select material for ${elementInfo.linerIds[0]}`
            }
            return "Select sensor"
        },
        [elementInfo],
    )

    return (
        <div ref={onRefChange} style={position} className={styles.materialContainer}>
            <div>{resolveLabel(elementInfo)}</div>
            <div className={styles.materialButtonsContainer}>
                <Select sorted={false} options={options} value={selectedOption} onChange={optionChangeHandler} />
                <Button onClick={submitSelection} disabled={!selectedOption} primary>
                    Select
                </Button>
                <Button onClick={onCancelLinerSelection} subtle>
                    Cancel
                </Button>
            </div>
        </div>
    )
}
